import NextLink from "common/bit/NextLink"
import { Font } from "components/shopFeatures/wizard/constants"
import styled, { css, keyframes } from "styled-components"
import { HomePageContainer } from "theme/shared"

const MOBILE = "@media (max-width: 820px)"
const SMALL_LAPTOP = "@media (max-width: 1280px)"

/* Scroll-reveal: each card owns its own useInView hook. Cards start at
   opacity:0; when a card enters the viewport its component adds `.in-view`
   and it plays this entrance (staggered via an inline animation-delay). */
const revealIn = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: none; }

  `
const revealInCard = keyframes`
  from { opacity: 0; transform: translateX(28px); }
  to { opacity: 1; transform: none; }

  `
/* Mixin shared by FeaturedCard and CardLink: hidden until `.in-view`, then the
   entrance runs and holds its end state (`both`). Reduced motion → just show. */
const reveal = css`
  &.be-reveal {
    opacity: 0;
  }
  &.be-reveal.in-view {
    animation: ${revealIn} 0.8s ease both;
  }
  @media (prefers-reduced-motion: reduce) {
    &.be-reveal {
      opacity: 1;
      animation: none;
    }
  }
`

const revealCard = css`
  &.be-reveal {
    opacity: 0;
  }
  &.be-reveal.in-view {
    animation: ${revealInCard} 0.8s ease both;
  }
  @media (prefers-reduced-motion: reduce) {
    &.be-reveal {
      opacity: 1;
      animation: none;
    }
  }
`

export const StoriesWrap = styled.section`
  width: 100%;
  background: ${(p) => p.theme.colors.white};
  padding-block: 64px;
  ${MOBILE} {
    padding-top: 32px;
    padding-bottom: 48px;
  }
`
/* ---- Header ---- */
export const SecHead = styled(HomePageContainer)`
  display: flex;
  justify-content: space-between;
  margin-bottom: 64px;
  align-items: flex-end;
  ${MOBILE} {
    flex-direction: column;
    align-items: flex-start; /* right in RTL */
    margin-bottom: 32px;
  }
`
export const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;
  ${MOBILE} {
    gap: 1px;
  }
`
export const Eyebrow = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 4px;
  color: ${(p) => p.theme.colors.newPrimaryRed};
`
export const Headline = styled.h2`
  margin: 0;
  max-width: 700px;
  font-family: ${Font.OENaPro};
  font-weight: 300;
  font-size: 40px;
  line-height: 1.2;
  color: ${(p) => p.theme.colors.darkBlack};
  ${MOBILE} {
    font-size: 32px;
  }
`
export const AllStoriesLink = styled(NextLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0.72px;
  color: ${(p) => p.theme.colors.darkBlack};
  text-decoration: none;
  padding-bottom: 4px;
  white-space: nowrap;
  svg {
    transform: scaleX(-1); /* point the chevron toward the start (RTL) */
  }
`
/* ---- Editorial layout ---- */
export const Wrap = styled(HomePageContainer)`
  width: 100%;
  margin: 0 auto;
`
export const Editorial = styled.div`
  display: grid;
  grid-template-columns: minmax(425px, 1fr) 700px;
  align-items: stretch;
  gap: 24px;
  /* Two-column (≥1280px): no fixed height. The grid row auto-sizes to the side
     column, and the featured card stretches to match it (align-items: stretch),
     so both columns end on the same baseline — no dangling gap (DLK-855). */
  @media (max-width: 1279px) {
    grid-template-columns: minmax(425px, 1fr);
    min-height: 580px;
  }
  ${MOBILE} {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`
export const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`
/* ---- Featured (desktop overlay) ---- */
export const FeaturedCard = styled.article`
  ${reveal}
  position: relative;
  min-height: 580px;
  /* Two-column layout: drop the floor so the card stretches to the grid row
     (driven by the side column) instead of forcing a 580px gap (DLK-855). The
     floor is kept above for the single-column range, where it is stacked. */
  @media (min-width: 1280px) {
    min-height: 0;
  }
  display: block;
  overflow: hidden;
  text-decoration: none;
  background: ${(p) => p.theme.colors.darkBlack};
  transition: box-shadow 0.5s ease, background 0.5s ease, transform 0.5s ease;
  img {
    transition: transform 0.5s ease;
  }
  &:hover {
    img {
      transform: scale(1.05);
    }
  }
  ${MOBILE} {
    display: none; /* mobile renders the featured as a stacked StoryCard instead */
  }
`
/* Renders children only on mobile (≤820px); display:contents keeps flex layout intact. */
export const MobileOnly = styled.div`
  display: contents;
  @media (min-width: 821px) {
    display: none;
  }
`
/* Renders children only on desktop (>820px); display:contents keeps flex layout intact. */
export const DesktopOnly = styled.div`
  display: contents;
  ${MOBILE} {
    display: none;
  }
`
export const FeaturedImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`
export const FeaturedScrim = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0) 35%,
    rgba(0, 0, 0, 0.85) 100%
  );
`
export const FeaturedTag = styled.div`
  position: absolute;
  top: 32px;
  right: 36px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2;
`
export const TagPill = styled.span`
  background: ${(p) => p.theme.colors.newPrimaryRed};
  color: ${(p) => p.theme.colors.white};
  font-family: ${Font.OENaPro};
  font-size: 14px;
  letter-spacing: 1.68px;
  line-height: 24px;
  padding: 4px 12px;
`
export const TagCategory = styled.span`
  color: ${(p) => p.theme.colors.white};
  font-family: ${Font.OENaPro};
  font-size: 14px;
  letter-spacing: 2.86px;
  line-height: 24px;
`
export const TagBar = styled.span`
  width: 20px;
  height: 3px;
  background: ${(p) => p.theme.colors.newPrimaryRed};
`
export const FeaturedTextWrap = styled.div`
  position: absolute;
  bottom: 36px;
  left: 36px;
  right: 36px;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* flex-start = right in RTL (title + meta right-aligned) */
  gap: 24px;
  z-index: 2;
  max-width: 401px;
  ${SMALL_LAPTOP} {
    max-width: 289px;
  }
  ${MOBILE} {
    width: 305px;
    position: static;
    max-width: 100%;
  }
`
export const FeaturedTitle = styled.h3`
  margin: 0;
  width: 100%;
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 40px;
  line-height: 48px;
  color: ${(p) => p.theme.colors.white};
  text-align: right;
`
export const FeaturedArrow = styled(NextLink)`
  position: absolute;
  bottom: 36px;
  left: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: 2px solid ${(p) => p.theme.colors.white};
  border-radius: 50%;
  color: ${(p) => p.theme.colors.white};
  z-index: 2;
  transition: background 0.25s ease, color 0.25s ease;
  svg {
    transform: scaleX(-1);
  }
  ${FeaturedCard}:hover & {
    background: ${(p) => p.theme.colors.white};
    color: ${(p) => p.theme.colors.darkBlack};
  }
`
/* ---- Story card (side cards desktop; all cards mobile) ---- */
export const CardLink = styled.article`
  ${revealCard}
  display: flex;
  background: ${(p) => p.theme.colors.neutral200 || "#F5F3F1"};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.5s ease, background 0.5s ease, transform 0.5s ease;
  img {
    transition: transform 0.5s ease;
  }
  &:hover {
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
    background: ${(p) => p.theme.colors.white};
    img {
      transform: scale(1.05);
    }
  }
  ${MOBILE} {
    flex-direction: column;
    flex: none;
  }
`
export const CardImg = styled.img`
  width: 350px;
  object-fit: cover;
  flex-shrink: 0;
  ${MOBILE} {
    width: 100%;
    height: 267px;
  }
`
export const CardBody = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* flex-start = right in RTL */
  justify-content: center;
  gap: 8px;
  padding: 40px 32px;
  text-align: right;
  ${MOBILE} {
    justify-content: flex-start;
    padding: 24px;
  }
`
export const CardTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
export const CardCategory = styled.span`
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 4px;
  color: ${(p) => p.theme.colors.darkBlack};
  ${MOBILE} {
    font-size: 14px;
    letter-spacing: 2px;
  }
`
export const CardBar = styled.span`
  width: 16px;
  height: 2px;
  background: ${(p) => p.theme.colors.newPrimaryRed};
`
export const CardTitle = styled.h3`
  margin: 0;
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 24px;
  line-height: 28px;
  max-width: 255px;
  color: ${(p) => p.theme.colors.darkBlack};
  text-align: right;
  ${MOBILE} {
    font-size: 18px;
    line-height: 24px;
    max-width: 257px;
  }
`
/* ---- Shared meta (read time · dot · date) ---- */
export const Meta = styled.div<{ $onDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${Font.OENaPro};
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  color: ${(p) => (p.$onDark ? p.theme.colors.white : p.theme.colors.neutral)};
  ${MOBILE} {
    font-size: 14px;
  }
`
export const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 2px;
  background: currentColor;
  opacity: 0.5;
`
/* ---- "לקריאה מלאה" link (cards) ---- */
export const ReadMore = styled(NextLink)`
  display: inline-flex;
  width: fit-content;
  text-decoration: none;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-bottom: 4px;
  border-bottom: 2px solid ${(p) => p.theme.colors.newPrimaryRed};
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: ${(p) => p.theme.colors.newPrimaryRed};
  svg {
    transform: scaleX(-1);
  }
`
